import React, { useEffect, useMemo, useState } from 'react';
import { Divider } from 'primereact/divider';
import EventService from '../../service/EventsService';
import { countryList } from '../../utilities/constants';
import { isTvrRegionOutput } from '../../utilities/tvrRegionOutputs';
import { getTimeInEtTimezone } from '../../utilities/functions';
import { isCenterCircleOutput } from '../../utilities/centerCirclePlaylist';
import { useHistory } from 'react-router-dom';

const DEFAULT_FLAG = 'assets/demo/images/flags/flag-placeholder-white.jpg';

const EventTooltipPanel = ({
  id,
  events,
  openEventPage,
  openPlaylistPage,
  onClose,
  isNhlUser,
  userClubId,
}) => {
  const [eventVenueFeeds, setEventVenueFeeds] = useState(null);
  const [flagImage, setFlagImage] = useState({
    src: DEFAULT_FLAG,
    className: '',
    city: '',
  });

  const history = useHistory();

  const event = useMemo(() => {
    const numericId = Number(id);
    return events.find(ev => ev.id === numericId);
  }, [events, id]);

  const getFlagClass = region => {
    const country = countryList.find(obj => obj.name === region);
    if (!country) return 'flag flag-ca';
    return `flag flag-${country.code.toLowerCase()}`;
  };

  const getFlagSrc = region => {
    return region === 'United States'
      ? 'assets/demo/images/flags/us-flag.png'
      : null;
  };

  useEffect(() => {
    let alive = true;

    const loadData = async eventId => {
      const eventService = new EventService();
      const data = await eventService.getCalendarEventData(eventId);

      if (!alive) return;

      if (data) {
        setEventVenueFeeds(data);

        if (data.venue && data.venue[0]) {
          const { region, city } = data.venue[0];
          const isUnitedStates = region === 'United States';

          setFlagImage({
            src: isUnitedStates ? getFlagSrc(region) : null,
            city: city || '',
            className: isUnitedStates ? '' : getFlagClass(region),
          });
        }
      } else {
        setEventVenueFeeds(null);
      }
    };

    if (id) loadData(id);

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!event) return null;

  const outputs = eventVenueFeeds?.outputs || [];
  const filteredOutputs = outputs.filter(
    output =>
      ![
        'FI-TV',
        'NOR-TV',
        'SWE-TV',
        'Viaplay Stream',
        'SLOVAK',
        '2 Spots',
        '10 Spots',
        'DAZN CZ',
        'DAZN DE',
        'DAZN ES',
        'DAZN FR',
        'DAZN SK',
        'DAZN UK',
        'DAZN WW',
        'ITV4 UK',
      ].includes(output.name) && !isTvrRegionOutput(output.name)
  );

  const toBool = value => value === true || value === 1 || value === '1';
  const canAccessCenterCircle =
    Number(userClubId) === 1 || Number(userClubId) === Number(event.home);

  const getStatuses = output => {
    if (!isNhlUser) {
      return {
        DED: false,
        SV: false,
        GV: false,
        II: false,
        CCZ: false,
      };
    }

    // show statuses only to NHL users
    return {
      DED: toBool(output.ded_complete),
      SV: toBool(output.sv_complete),
      GV: toBool(output.gv_complete),
      II: toBool(output.ice_complete),
      CCZ: toBool(output.ccz_complete),
    };
  };

  const openSpecificPlaylistPage = (eventClick, outputId, playlistType) => {
    if (!outputId) return;

    const playlistRoutes = {
      DED: '/playlist',
      SV: '/playlist-sv',
      GV: '/playlist-gv',
      II: '/playlist-ii',
      CCZ: '/playlist-ccz',
    };

    const url = `${playlistRoutes[playlistType]}/${outputId}`;

    // Ctrl (Windows/Linux) or Cmd (Mac)
    if (eventClick.ctrlKey || eventClick.metaKey) {
      window.open(`${window.location.origin}/#${url}`, '_blank');
    } else {
      history.push({ pathname: url, state: {} });
      onClose();
    }
  };

  const renderCell = ok => {
    if (ok) {
      return <i className="pi pi-check calendarTooltipStatusIcon" />;
    }

    return <span className="calendarTooltipStatusEmpty">—</span>;
  };

  const renderClickableCell = (ok, output, playlistType) => (
    <div
      className={`feedsClickableCell ${
        ok
          ? 'feedsClickableCell--ready'
          : 'feedsClickableCell--empty'
      }`}
      onClick={eventClick =>
        openSpecificPlaylistPage(
          eventClick,
          output.id,
          playlistType
        )
      }
      title={`Click to open ${playlistType} playlists • Ctrl+Click to open in new tab`}
    >
      {renderCell(ok)}
    </div>
  );

  const competition =
    event.id > 2025021400 && event.id < 2026010000
      ? 'Playoffs'
      : 'Regular Season';

  return (
    <div className="calendarEventTooltipPanel">
      <div className="calendarEventTooltipPanel__header">
        <button
          type="button"
          className="calendarEventTooltipPanel__event"
          onClick={() => {
            openEventPage(event);
            onClose();
          }}
          title="Open event page"
        >
          <span className="calendarEventTooltipPanel__eventIcon">
            <i className="pi pi-calendar"></i>
          </span>
          <span className="calendarEventTooltipPanel__eventText">
            <small>Game details</small>
            <strong>{event.title}</strong>
          </span>
          <i className="pi pi-external-link calendarEventTooltipPanel__openIcon"></i>
        </button>

        <button
          type="button"
          className="calendarEventTooltipPanel__close"
          onClick={onClose}
          title="Close"
          aria-label="Close event details"
        >
          <i className="pi pi-times"></i>
        </button>
      </div>

      <div className="calendarEventTooltipBody">
        <div className="calendarEventTooltipMeta">
          <div className="calendarEventTooltipMeta__row">
            <span className="calendarEventTooltipMeta__label">
              Competition
            </span>
            <strong className="calendarEventTooltipMeta__value">
              {competition}
            </strong>
          </div>

          <div className="calendarEventTooltipMeta__row">
            <span className="calendarEventTooltipVenue">
              <span className="calendarEventTooltipVenue__flag">
                {flagImage.src ? (
                  <img
                    src={flagImage.src}
                    alt=""
                    width={26}
                    height={18}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className={flagImage.className}
                  />
                )}
              </span>
              <span className="calendarEventTooltipVenue__city">
                {flagImage.city || 'Venue unavailable'}
              </span>
            </span>

            <strong className="calendarEventTooltipMeta__time">
              {getTimeInEtTimezone(event.et_time || event.et, false)}
            </strong>
          </div>
        </div>

        {filteredOutputs.length > 0 && (
          <>
            <Divider
              align="center"
              className="calendarEventTooltipDivider"
            >
              <span className="calendarEventTooltipDivider__label">
                <i className="pi pi-list"></i>
                Feeds
              </span>
            </Divider>

            <div className="feedsTable calendarTooltipFeedsTable">
              <div className="feedsTableRow feedsTableHeader">
                <div className="feedsColName">Feed</div>
                <div className="feedsCol">DED</div>
                <div className="feedsCol">SV</div>
                <div className="feedsCol">GV</div>
                <div className="feedsCol">Ice</div>
                <div className="feedsCol">CCZ</div>
              </div>

              {filteredOutputs.map((output, index) => {
                const statuses = getStatuses(output);

                return (
                  <div
                    key={output.id ?? index}
                    className={`feedsTableRow ${
                      index % 2 === 0 ? 'feedsRowAlt' : ''
                    }`}
                  >
                    <div
                      className="feedsColName feedsLink"
                      onClick={eventClick => {
                        openPlaylistPage(output.id, eventClick);
                        onClose();
                      }}
                      title="Open DED playlist"
                    >
                      {output.name}
                    </div>

                    <div className="feedsCol">
                      {renderClickableCell(
                        statuses.DED,
                        output,
                        'DED'
                      )}
                    </div>
                    <div className="feedsCol">
                      {renderClickableCell(
                        statuses.SV,
                        output,
                        'SV'
                      )}
                    </div>
                    <div className="feedsCol">
                      {renderClickableCell(
                        statuses.GV,
                        output,
                        'GV'
                      )}
                    </div>
                    <div className="feedsCol">
                      {renderClickableCell(
                        statuses.II,
                        output,
                        'II'
                      )}
                    </div>
                    <div className="feedsCol">
                      {isCenterCircleOutput(output.name) && canAccessCenterCircle ? (
                        renderClickableCell(
                          statuses.CCZ,
                          output,
                          'CCZ'
                        )
                      ) : (
                        <div
                          className="feedsClickableCell feedsClickableCell--empty"
                          title={
                            isCenterCircleOutput(output.name)
                              ? 'Center Circle is available only to NHL and the home club'
                              : 'Center Circle is not available for this feed'
                          }
                        >
                          {renderCell(false)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EventTooltipPanel;
